import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuList from "./panels/Appdrawer/DrawerList";
import { HiMenu } from "react-icons/hi";
import pxToRem from "../../Themes/Functions/pxToRem";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import WDLoader from "../../Components/AppLoader/WDLoader";
import WDSuccessToast from "../../Components/SuccessToast";
import WDErrorToast from "../../Components/ErrorToast";
import { useDispatch, useSelector } from "react-redux";
import {
  dismissErrorMessage,
  dismissSuccessMessage,
  updateAuthInfo,
} from "../AppLayout/redux/AppSlice";
import { Avatar, Button, Chip, Grid, Menu, MenuItem } from "@mui/material";
import { IoMdLogOut } from "react-icons/io";
import WDButton from "../../MaterialComponents/WDButton";
import AppService from "../../Configurations/Appservice";
import { NotificationBell } from "../../Components/Notifications";

const drawerWidth = 240;

let backgroundValue = "#064d51"; // Changed from blue (#065594) to teal to match ticket detail view

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  background: backgroundValue,
  borderTopRightRadius: 15,
  borderBottomRightRadius: 15,
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  background: backgroundValue,
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  borderTopRightRadius: 15,
  borderBottomRightRadius: 15,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const DrawerOpenHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: `calc(100% - ${65}px)`,
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  })
);

export default function MiniDrawer() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);

  const dispatch = useDispatch();
  const appStore = useSelector((state) => state.app);
  const principalInfo = appStore.auth?.principalInfo || {};
  const navigate = useNavigate();
  
  // State for organization code
  const [organizationCode, setOrganizationCode] = React.useState('');
  const [organizationName, setOrganizationName] = React.useState('');
  // Sidebar avatar (top-left) source
  const [avatarSrc, setAvatarSrc] = React.useState('');
  const [avatarVersion, setAvatarVersion] = React.useState(() => (typeof window !== 'undefined' && localStorage.getItem('profilePhotoUpdatedAt')) || '');

  // Auth token helper
  const getAuthToken = () => {
    try {
      const authInfo = localStorage.getItem('authInfo');
      if (authInfo) {
        const obj = JSON.parse(authInfo);
        if (obj && obj.token) return obj.token;
      }
    } catch {}
    return localStorage.getItem('token') || localStorage.getItem('jwt_token');
  };

  // Load avatar from backend bytes endpoint
  React.useEffect(() => {
    let isMounted = true;

    const fetchAvatar = async (filePath) => {
      try {
        if (!filePath) {
          if (isMounted) setAvatarSrc('');
          return;
        }
        const token = getAuthToken();
        const url = `/v1/files/download/bytes?filePath=${encodeURIComponent(filePath)}${token ? `&auth_token=${encodeURIComponent(token)}` : ''}`;
        const res = await fetch(url, { method: 'GET' });
        if (!res.ok) throw new Error(`avatar fetch ${res.status}`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        if (isMounted) setAvatarSrc((prev) => {
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          return objectUrl;
        });
      } catch {
        if (isMounted) setAvatarSrc('');
      }
    };

    const loadProfilePhotoPath = async () => {
      try {
        const token = getAuthToken();
        const res = await fetch('/v1/users/me', {
          headers: token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        const path = data?.profilePhotoPath || data?.profileUrl;
        if (path) await fetchAvatar(path);
      } catch {}
    };

    // Load order: localStorage cached path first, then backend
    (async () => {
      const cachedPath = typeof window !== 'undefined' ? localStorage.getItem('profilePhotoPath') : null;
      if (cachedPath) await fetchAvatar(cachedPath);
      await loadProfilePhotoPath();
    })();

    // React to profile updates from other tabs
    const onStorage = (e) => {
      if (e.key === 'profilePhotoUpdatedAt') {
        setAvatarVersion(e.newValue || String(Date.now()));
        const newPath = localStorage.getItem('profilePhotoPath');
        if (newPath) fetchAvatar(newPath);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', onStorage);
      setAvatarSrc((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarVersion]);

  // Get organization code from JWT token or API
  React.useEffect(() => {
    const getOrganizationInfo = async () => {
      try {
        // Try multiple token sources
        let token = null;
        let authInfo = null;
        
        // Try to get from authInfo first
        const authInfoStr = localStorage.getItem('authInfo');
        if (authInfoStr) {
          try {
            authInfo = JSON.parse(authInfoStr);
            if (authInfo && authInfo.token) {
              token = authInfo.token;
              console.log('Found token in authInfo');
            }
          } catch (e) {
            console.error('Error parsing authInfo:', e);
          }
        }
        
        // If no token in authInfo, try jwt_token
        if (!token) {
          token = localStorage.getItem('jwt_token');
          if (token) {
            console.log('Found token in jwt_token');
          }
        }
        
        // If we have a token, extract org ID and get org details
        if (token) {
          let orgId = null;
          
          // Try to get org ID from JWT payload
          if (token.startsWith('ey')) {
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                console.log('JWT payload for org code:', payload);
                
                if (payload.org) {
                  orgId = payload.org;
                  console.log('Found org ID in JWT payload:', orgId);
                }
              }
            } catch (e) {
              console.error('Error parsing JWT token:', e);
            }
          }
          
          // If we couldn't get org ID from JWT, try from authInfo
          if (!orgId && authInfo && authInfo.principalInfo && authInfo.principalInfo.organisationId) {
            orgId = authInfo.principalInfo.organisationId;
            console.log('Found org ID in authInfo:', orgId);
          }
          
          // If we have an org ID, get org details
          if (orgId) {
            try {
              console.log('Organization ID from JWT (AppLayout):', orgId);
              
              // Use AppService instead of fetch
              const response = await AppService.get(`v1/organisation/details`, {
                params: { organisationId: orgId }
              });
              
              console.log('Organization data:', response);
              
              if (response) {
                console.log('Using API response for organization data');
                
                // Use real data from the API
                const orgName = response.name || '';
                const orgId = response.id || '';
                
                // Get organization code directly from the API response
                let orgCode = response.code || '';
                
                // If code is not provided by the API, generate a fallback code
                if (!orgCode) {
                  console.log('Organization code not found in API response, generating fallback code');
                  
                  // Use organization ID to determine the code (fallback logic)
                  if (orgId === '94a8adfb-c8c6-3e45-b0f8-14797cbe0d48') {
                    orgCode = 'ORG'; // Customer 3
                  } else if (orgId === '6db8544d-dfd6-396d-8f7e-1fce7ca10c64') {
                    orgCode = 'CU1'; // Customer 1
                  } else if (orgId === 'bacde52d-23d3-3021-8429-b7cec186774d') {
                    orgCode = 'CBC'; // CBC
                  } else if (orgId === 'ed5b343b-30c0-4949-b704-412f80e55d31') {
                    orgCode = 'THA'; // THASHEEL
                  } else {
                    // If no mapping exists, use the first 3 letters of the organization name
                    orgCode = orgName.substring(0, Math.min(3, orgName.length)).toUpperCase();
                  }
                  
                  // Show alert for missing organization code
                  alert('Organization code not found. Please contact your administrator.');
                }
                
                console.log('Using organization code:', orgCode, 'for organization:', orgName, 'with ID:', orgId);
                setOrganizationCode(orgCode);
                
                if (orgName) {
                  console.log('Setting organization name:', orgName);
                  setOrganizationName(orgName);
                }
              } else {
                console.log('No organization data in API response');
              }
            } catch (e) {
              console.error('Error fetching org data:', e);
              console.log('Keeping existing organization data');
              
              // If we can't get organization data from the API, try to extract it from the JWT token
              try {
                const token = localStorage.getItem('jwt_token');
                if (token && token.startsWith('ey')) {
                  const payload = JSON.parse(atob(token.split('.')[1]));
                  console.log('JWT payload:', payload);
                  
                  // Look for organization info in the JWT payload
                  if (payload.organization_name) {
                    setOrganizationName(payload.organization_name);
                  }
                  if (payload.organization_code) {
                    setOrganizationCode(payload.organization_code);
                  }
                }
              } catch (jwtError) {
                console.error('Error extracting organization from JWT:', jwtError);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error getting organization info:', error);
      }
    };
    
    getOrganizationInfo();
  }, []);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const _logoutHandler = () => {
    console.log('AppLayout2: Calling logout function');
    // Import and use the proper logout function from services/auth.js
    import('../../services/auth').then(authService => {
      authService.logout();
      // Redirect to home page after successful logout
      console.log('AppLayout2: Redirecting to home page after logout');
      navigate("/");
    }).catch(error => {
      console.error('Error during logout:', error);
      // Fallback to basic navigation if import fails
      navigate("/");
    });
  };

  // let authInfo = localStorage.getItem("authInfo");
  // if (authInfo) {
  //   sessionStorage.setItem("authInfo", authInfo);
  //   const authInfoObject = JSON.parse(authInfo);
  //   dispatch(updateAuthInfo(authInfoObject));
  //   let url = "";
  //   if (authInfoObject.principalInfo.userType === "ADMIN_ORG") {
  //     url = "ADM-Dash/customer";
  //   } else if (authInfoObject.principalInfo.userType === "PARTNER_ORG") {
  //     url = "ptr/dash";
  //   } else {
  //     url = "cst/dash";
  //   }
  //   return <Navigate to={url} replace={true} />;
  // }

  return (
    <Box sx={{ display: "flex" }}>
      <WDLoader open={appStore.showLoader} />
      <WDSuccessToast
        open={appStore.successMessage.show}
        onClose={() => dispatch(dismissSuccessMessage())}
        message={appStore.successMessage.message}
      />
      <WDErrorToast
        open={appStore.errorMessage.show}
        onClose={() => dispatch(dismissErrorMessage())}
        message={appStore.errorMessage.message}
      />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          {appStore.screenTitle ? (
            <Typography variant="h5" color="primary.main" noWrap sx={{}}>
              {appStore.screenTitle}
            </Typography>
          ) : null}
          {/* Organization code chip */}
          {organizationCode && (
            <Chip
              label={`${organizationName} (${organizationCode})`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ ml: 2, fontWeight: 'bold' }}
            />
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "row-reverse",
              alignItems: "center",
              marginLeft: "auto",
              gap: 2,
              // backgroundColor: "green",
            }}
          >
            <IoMdLogOut color="primary" onClick={handleClick} style={{ cursor: "pointer" }} />
            <NotificationBell sx={{ color: 'primary.main' }} />
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <Box p={2}>
                <Typography fontSize={14}>Are you sure you want to logout ?</Typography>
              </Box>
              <Grid container>
                <Grid item md={6}>
                  <WDButton variant="text" onClick={handleClose}>
                    cancel
                  </WDButton>
                </Grid>
                <Grid item md={6}>
                  <WDButton variant="text" onClick={_logoutHandler}>
                    logout
                  </WDButton>
                </Grid>
              </Grid>
            </Menu>
            {/* <Box
              sx={{
                p: 0.5,
                display: "flex",
                // border: 1,
                justifyContent: "center",
                alignItems: "center",
                borderRadius: pxToRem(10),
                mx: 3,
              }}
            >
              <Avatar
                alt="Remy Sharp"
                src="https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500"
                sx={{ width: 30, height: 30, mx: 1 }}
              />
              <Box>
                <Typography fontSize={10} variant="h6">
                  Jaseem bin muhammed
                </Typography>
                <Typography fontSize={10}>Org1</Typography>
              </Box>
            </Box> */}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        {!open && (
          <DrawerHeader>
            <IconButton color="white" aria-label="open drawer" onClick={handleDrawerOpen}>
              <HiMenu />
            </IconButton>
          </DrawerHeader>
        )}
        {open && (
          <DrawerOpenHeader>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                // bgcolor: "red",
                justifyContent: "center",
                alignItems: "center",
                paddingLeft: pxToRem(10),
              }}
            >
              <Typography variant="h6" color="white.main">
                Thaklis
              </Typography>
            </Box>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon color="white" />
            </IconButton>
          </DrawerOpenHeader>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex" }} p={1} pb={3}>
            <Avatar
              alt={principalInfo?.name || 'User'}
              src={avatarSrc || undefined}
              sx={{ width: 40, height: 40, mx: 1 }}
            />
            <Box>
              <Typography fontSize={14} color="#ffffff" variant="h6">
                {principalInfo.name}
              </Typography>
              <Typography fontSize={14} sx={{ lineHeight: 1 }} color="white.main">
                {principalInfo.organisationName}
              </Typography>
            </Box>
          </Box>
          <MenuList open={open} minSideNav={!open} />
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: 2,
          pt: 0,
          bgcolor: "#e6f0f0", // Changed from light blue (#E2EBFC) to light teal to complement the sidebar
          pt: pxToRem(64),
          height: "100vh",
        }}
      >
        {/* <DrawerHeader /> */}
        <Outlet />
      </Box>
    </Box>
  );
}
